// pages/ConfigurationPage/NotificationRulesManager.jsx
import { useState } from 'react';
import { 
  PlayIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ChartBarIcon,
  ArrowPathIcon,
  PencilIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useNotificationRules } from '../../../hooks/useNotificationRules';
import ScheduleEditorModal from './ScheduleEditorModal';

const NotificationRulesManager = () => {
  const {
    rules,
    loading,
    toggleRule,
    updateSchedule,
    updateRoles,
    runRuleNow,
  } = useNotificationRules();

  const [editingSchedule, setEditingSchedule] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [runningRules, setRunningRules] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState(null);

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-100 text-red-700',
      warning: 'bg-orange-100 text-orange-700',
      info: 'bg-blue-100 text-blue-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[severity]}`}>
        {severity}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const styles = {
      financial: 'bg-green-100 text-green-700',
      operational: 'bg-purple-100 text-purple-700',
      team: 'bg-indigo-100 text-indigo-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[category]}`}>
        {category}
      </span>
    );
  };

  const getScheduleDisplay = (scheduleType, scheduleConfig) => {
    switch (scheduleType) {
      case 'daily':
        return `Daily ${scheduleConfig.time}`;
      case 'weekly':
        return `${scheduleConfig.day_of_week} ${scheduleConfig.time}`;
      case 'monthly':
        return `Monthly (${scheduleConfig.day}) ${scheduleConfig.time}`;
      case 'twice_monthly':
        return `2x/mo (${scheduleConfig.days.join(',')}) ${scheduleConfig.time}`;
      case 'every_n_days':
        return `Every ${scheduleConfig.interval_days}d ${scheduleConfig.time}`;
      default:
        return 'Not scheduled';
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await toggleRule(rule.rule_id, rule.enabled);
      showSuccess(`Rule ${rule.enabled ? 'disabled' : 'enabled'} successfully`);
    } catch (err) {
      console.error('Error toggling rule:', err);
    }
  };

  const handleSaveSchedule = async (scheduleType, scheduleConfig) => {
    if (!editingSchedule) return;
    
    try {
      const response = await updateSchedule(
        editingSchedule.rule_id,
        scheduleType,
        scheduleConfig
      );
      setEditingSchedule(null);
      showSuccess(response.message || 'Schedule updated successfully');
    } catch (err) {
      console.error('Error updating schedule:', err);
    }
  };

  const handleRoleToggle = async (rule, role) => {
    const currentRoles = rule.allowed_roles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    
    try {
      await updateRoles(rule.rule_id, newRoles);
    } catch (err) {
      console.error('Error updating roles:', err);
    }
  };

  const handleRunNow = async (rule) => {
    setRunningRules(prev => new Set(prev).add(rule.rule_id));
    
    try {
      const response = await runRuleNow(rule.rule_id);
      showSuccess(
        response.message || 
        `Rule executed: ${response.notifications_created || 0} notification(s) created`
      );
    } catch (err) {
      console.error('Error running rule:', err);
    } finally {
      setRunningRules(prev => {
        const newSet = new Set(prev);
        newSet.delete(rule.rule_id);
        return newSet;
      });
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const toggleExpandRow = (ruleId) => {
    setExpandedRow(expandedRow === ruleId ? null : ruleId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                {/* Expand icon */}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rule Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Schedule
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visible To
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rules.map((rule) => (
              <>
                {/* Main Row */}
                <tr 
                  key={rule.rule_id}
                  className={`hover:bg-gray-50 transition-colors ${
                    expandedRow === rule.rule_id ? 'bg-blue-50' : ''
                  }`}
                >
                  {/* Expand Button */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleExpandRow(rule.rule_id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <InformationCircleIcon className="h-5 w-5" />
                    </button>
                  </td>

                  {/* Rule Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {rule.rule_name}
                      </span>
                      {getSeverityBadge(rule.severity)}
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-4 py-3">
                    {getCategoryBadge(rule.category)}
                  </td>

                  {/* Schedule */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">
                        {getScheduleDisplay(rule.schedule_type, rule.schedule_config)}
                      </span>
                      <button
                        onClick={() => setEditingSchedule(rule)}
                        className="text-blue-600 hover:text-blue-700"
                        title="Edit schedule"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {rule.next_run_time && (
                      <div className="text-xs text-gray-400 mt-1">
                        Next: {new Date(rule.next_run_time).toLocaleDateString()} {new Date(rule.next_run_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    )}
                  </td>

                  {/* Visible To (Roles) */}
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {['admin', 'manager', 'user'].map((role) => (
                        <label
                          key={role}
                          className={`px-2 py-1 text-xs rounded cursor-pointer transition-colors ${
                            rule.allowed_roles?.includes(role)
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                          title={`Toggle ${role} access`}
                        >
                          <input
                            type="checkbox"
                            checked={rule.allowed_roles?.includes(role) || false}
                            onChange={() => handleRoleToggle(rule, role)}
                            className="sr-only"
                          />
                          {role.charAt(0).toUpperCase()}
                        </label>
                      ))}
                    </div>
                  </td>

                  {/* Status Toggle */}
                  <td className="px-4 py-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rule.enabled}
                        onChange={() => handleToggleRule(rule)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRunNow(rule)}
                        disabled={runningRules.has(rule.rule_id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          runningRules.has(rule.rule_id)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        title="Run now"
                      >
                        {runningRules.has(rule.rule_id) ? (
                          <ArrowPathIcon className="h-3 w-3 animate-spin" />
                        ) : (
                          <PlayIcon className="h-3 w-3" />
                        )}
                        Run
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded Row - Details */}
                {expandedRow === rule.rule_id && (
                  <tr>
                    <td colSpan="7" className="px-4 py-4 bg-gray-50">
                      <div className="space-y-3 text-sm">
                        {/* Description */}
                        <div>
                          <span className="font-medium text-gray-700">Description:</span>
                          <p className="text-gray-600 mt-1">{rule.description}</p>
                        </div>

                        {/* Resolution Steps */}
                        <div>
                          <span className="font-medium text-gray-700">Resolution Steps:</span>
                          <p className="text-gray-600 mt-1 whitespace-pre-wrap">
                            {rule.resolution_steps}
                          </p>
                        </div>

                        {/* Required Permissions */}
                        <div>
                          <span className="font-medium text-gray-700">Required Permissions:</span>
                          <div className="flex gap-1 mt-1">
                            {rule.required_permissions?.map((perm) => (
                              <span
                                key={perm}
                                className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs"
                              >
                                {perm}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Last Run */}
                        {rule.last_run_time && (
                          <div>
                            <span className="font-medium text-gray-700">Last Run:</span>
                            <span className="text-gray-600 ml-2">
                              {new Date(rule.last_run_time).toLocaleString()}
                            </span>
                          </div>
                        )}

                        {/* View History Button */}
                        <div className="pt-2">
                          <button
                            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <ChartBarIcon className="h-4 w-4" />
                            View Run History
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Schedule Editor Modal */}
      {editingSchedule && (
        <ScheduleEditorModal
          rule={editingSchedule}
          onSave={handleSaveSchedule}
          onClose={() => setEditingSchedule(null)}
        />
      )}
    </div>
  );
};

export default NotificationRulesManager;